"use client";

import MuxPlayerReact from "@mux/mux-player-react";
import { usePostHog } from "posthog-js/react";
import React from "react";

import {
  mergePlayerRefs,
  useWayflyerControlBarRef,
} from "@/components/video-player/use-wayflyer-control-bar";
import { cn } from "@/lib/utils";

// Eager counterpart of MuxPlayer for above-the-fold videos: the player element
// is bundled with the page instead of lazy-loaded on viewport intersection, so
// the play/seek controls are interactive as soon as the page hydrates.
type MuxPlayerEagerProps = React.ComponentPropsWithoutRef<
  typeof MuxPlayerReact
> & {
  /** Skip Wayflyer control bar injection — for chromeless animation-style players. */
  chromeless?: boolean;
};

export const MuxPlayerEager = React.forwardRef<
  React.ElementRef<typeof MuxPlayerReact>,
  MuxPlayerEagerProps
>(
  (
    {
      style = {},
      className,
      onPlay,
      preload = "metadata",
      chromeless = false,
      ...props
    },
    ref
  ) => {
    const posthog = usePostHog();
    const wayflyerRef = useWayflyerControlBarRef();

    return (
      <MuxPlayerReact
        ref={chromeless ? ref : mergePlayerRefs(ref, wayflyerRef)}
        streamType="on-demand"
        {...props}
        preload={preload}
        disableCookies={true}
        accentColor="var(--pulse-700)"
        primaryColor="var(--grey-0)"
        secondaryColor="transparent"
        className={cn(
          chromeless ? "wf-mux-player-chromeless" : "wf-mux-player",
          className
        )}
        onPlay={(e) => {
          posthog.capture("video_played", {
            title: props.title,
            id: props.playbackId,
          });
          onPlay?.(e);
        }}
        style={style}
      />
    );
  }
);

MuxPlayerEager.displayName = "MuxPlayerEager";
